import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Logo } from '@/components/common';
import { LANGUAGES, currentLanguage } from '@/i18n';
import { cn } from '@/lib/cn';

const UPDATED = '2026-10-04';

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="font-display text-xl">{title}</h2>
      <div className="mt-2 space-y-2 leading-relaxed text-ink-2 [&_li]:ml-5 [&_li]:list-disc [&_strong]:text-ink">
        {children}
      </div>
    </section>
  );
}

function Spanish() {
  return (
    <>
      <p className="mt-4 text-lg leading-relaxed">
        Tapete de Batalla es una herramienta gratuita y sin fines comerciales para preparar combates de Dungeons &amp;
        Dragons. Esta página explica qué datos guarda, para qué y cómo borrarlos.
      </p>
      <Section title="Qué datos se guardan">
        <ul>
          <li>
            <strong>Si entras con Google:</strong> tu email, tu nombre y la dirección de tu foto de perfil, que Google
            envía con tu permiso. No se recibe ni se guarda tu contraseña de Google ni nada más de tu cuenta.
          </li>
          <li>
            <strong>Si entras con usuario y contraseña:</strong> el nombre de usuario, un email y la contraseña
            transformada con un hash (scrypt). La contraseña nunca se guarda tal cual.
          </li>
          <li>
            <strong>Lo que creas:</strong> tus escenas (peanas, áreas, notas, orden de iniciativa), su historial de
            versiones y las imágenes de mapa que subas.
          </li>
          <li>
            <strong>Una cookie de sesión</strong> técnica para mantenerte dentro. No hay cookies de publicidad ni de
            seguimiento.
          </li>
        </ul>
      </Section>
      <Section title="Para qué se usan">
        <p>
          Solo para que puedas entrar y guardar tus escenas. Los datos{' '}
          <strong>no se venden, no se ceden y no se comparten</strong> con nadie. No hay publicidad ni herramientas de
          analítica. Cada usuario ve únicamente sus propias escenas.
        </p>
      </Section>
      <Section title="Dónde se guardan y cuánto tiempo">
        <p>
          En servidores de Hetzner en Núremberg (Alemania, Unión Europea). Se conservan mientras tengas cuenta. Hay una
          copia de seguridad diaria de la base de datos que se guarda 7 días.
        </p>
      </Section>
      <Section title="Cómo borrar tus datos">
        <p>
          Desde el menú de tu avatar, <strong>«Borrar mi cuenta»</strong>. Se borran al momento tu cuenta, tus escenas,
          su historial y tus mapas. De las copias de seguridad desaparecen como mucho en 7 días.
        </p>
      </Section>
      <Section title="Datos de Google">
        <p>
          El uso y la transferencia de la información recibida de las API de Google se ajustan a la{' '}
          <a
            className="text-accent underline"
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noreferrer"
          >
            Política de datos de usuario de los servicios de API de Google
          </a>
          , incluidos los requisitos de uso limitado.
        </p>
      </Section>
      <Section title="Cambios">
        <p>Si esta política cambia, se actualizará esta página y su fecha.</p>
      </Section>
    </>
  );
}

function English() {
  return (
    <>
      <p className="mt-4 text-lg leading-relaxed">
        Battle Mat is a free, non-commercial tool to plan Dungeons &amp; Dragons encounters. This page explains what
        data it stores, why, and how to delete it.
      </p>
      <Section title="What data is stored">
        <ul>
          <li>
            <strong>If you sign in with Google:</strong> your email, your name and the address of your profile picture,
            which Google sends with your permission. Your Google password and anything else in your account are never
            received or stored.
          </li>
          <li>
            <strong>If you sign in with a username and password:</strong> the username, an email and the password
            transformed with a hash (scrypt). The password itself is never stored.
          </li>
          <li>
            <strong>What you create:</strong> your scenes (tokens, areas, notes, initiative order), their version
            history and the map images you upload.
          </li>
          <li>
            <strong>A technical session cookie</strong> to keep you signed in. There are no advertising or tracking
            cookies.
          </li>
        </ul>
      </Section>
      <Section title="What it is used for">
        <p>
          Only to let you sign in and save your scenes. Data is <strong>not sold, transferred or shared</strong> with
          anyone. There is no advertising and no analytics. Each user only sees their own scenes.
        </p>
      </Section>
      <Section title="Where it is stored and for how long">
        <p>
          On Hetzner servers in Nuremberg (Germany, European Union), for as long as you have an account. A daily backup
          of the database is kept for 7 days.
        </p>
      </Section>
      <Section title="How to delete your data">
        <p>
          From your avatar menu, <strong>“Delete my account”</strong>. Your account, scenes, history and maps are
          deleted immediately. They disappear from the backups within 7 days.
        </p>
      </Section>
      <Section title="Google data">
        <p>
          The use and transfer of information received from Google APIs adheres to the{' '}
          <a
            className="text-accent underline"
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noreferrer"
          >
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>
      </Section>
      <Section title="Changes">
        <p>If this policy changes, this page and its date will be updated.</p>
      </Section>
    </>
  );
}

/** Política de privacidad pública (la pide Google para publicar el login). */
export function PrivacyPage() {
  const { t, i18n } = useTranslation();
  const lang = currentLanguage();
  return (
    <main className="min-h-full bg-surface">
      <div className="mx-auto max-w-2xl px-5 py-10">
        <div className="flex items-center justify-between gap-4">
          <Link to="/">
            <Logo />
          </Link>
          <div className="flex gap-1 text-sm">
            {LANGUAGES.map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => void i18n.changeLanguage(code)}
                className={cn(
                  'rounded px-2 py-0.5',
                  lang === code ? 'bg-surface-3 text-ink' : 'text-ink-2 hover:text-ink',
                )}
              >
                {t(`language.${code}`)}
              </button>
            ))}
          </div>
        </div>
        <h1 className="mt-10 font-display text-4xl">{t('privacy.title')}</h1>
        <p className="mt-1 text-sm text-ink-3">{t('privacy.updated', { date: UPDATED })}</p>
        {lang === 'en' ? <English /> : <Spanish />}
      </div>
    </main>
  );
}
